import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserModel } from 'app/account-settings/models/user.model';
import { UserService } from 'app/user/user.service';
import { CurrentContextService } from '../current-context.service';
import { CurrentSiteIdStore } from '../current-site-id.store';
import { IdentityService } from './identity.service';
import { isAccountAdminOn } from './site-role';

/** Whether the signed-in user is account admin on the open site. Staff are not counted; callers add isAdmin(). */
@Injectable({ providedIn: 'root' })
export class SiteRoleService {
  private readonly identityService = inject(IdentityService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);
  private readonly userService = inject(UserService);

  private readonly identity = toSignal(this.identityService.onIdentityChanged, {
    initialValue: this.identityService.currentIdentity,
  });
  private readonly context = toSignal(this.currentContextService.currentContextBS, {
    initialValue: this.currentContextService.currentContext,
  });

  // A site switch always reloads the context, so the store is read again then.
  public readonly isAccountAdmin = computed(() =>
    this.isAccountAdminFor(this.context()?.user, this.identity()?.isSiteAdmin),
  );

  /** For guards, which can run before the context has loaded the user. The gateway checks the role again on writes. */
  public async isAccountAdminAsync(): Promise<boolean> {
    const user =
      this.currentContextService.currentContext?.user ?? (await this.userService.getUser().catch(() => undefined));
    return this.isAccountAdminFor(user, this.identityService.currentIdentity?.isSiteAdmin);
  }

  private isAccountAdminFor(user: UserModel | undefined, isSiteAdmin: boolean | undefined): boolean {
    return isAccountAdminOn(user?.sites, this.currentSiteIdStore.get(), isSiteAdmin ?? false);
  }
}
